import React, { Component } from 'react';
import { Alert, Button, Col, Form, Row } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';

import globalTranslations from '../../../i18n/globalTranslations';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import { isEmail, notBlank, minLength } from '../../../util/formValidators';
import ApiWrapper from '../../../services/ApiWrapper';
import UnexpectedError from '../../loader/UnexpectedError';
import ConfigStore from '../../../store/ConfigStore';
import { modelOf } from '../../../prop-types';
import FormGroupField from '../../form/FormGroupField';
import AccountStore from '../../../store/AccountStore';
import ContentForState from '../../loader/ContentForState';

const UpdateStatus = {
  NONE: 'NONE',
  REQUESTING: 'REQUESTING',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SUCCESS: 'SUCCESS',
  FIRSTNAME_NOT_VALID: 'FIRSTNAME_NOT_VALID',
  LASTNAME_NOT_VALID: 'LASTNAME_NOT_VALID',
  EMAIL_NOT_VALID: 'EMAIL_NOT_VALID',
  TELEPHONE_NOT_VALID: 'TELEPHONE_NOT_VALID',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR'
};

@observer
export class MyAccountInfoForm extends Component {
  _isMounted = false;
  form = null;
  state = {
    status: UpdateStatus.NONE
  };

  constructor(props) {
    super(props);

    const { configStore } = this.props;

    const fields = {
      firstname: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.firstNameTitle} />,
          configStore.formValidation.firstName.minLength
        )
      ]),
      lastname: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.lastNameTitle} />,
          configStore.formValidation.lastName.minLength
        )
      ]),
      email: createFormField({}, [
        notBlank(<FormattedMessage {...globalTranslations.emailTitle} />),
        isEmail(<FormattedMessage {...globalTranslations.emailTitle} />)
      ]),
      telephone: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.telephoneTitle} />,
          configStore.formValidation.telephone.minLength
        )
      ])
    };
    this.form = createForm(fields);
  }

  componentDidMount() {
    this.maybeLoadAccountInfo();
  }

  maybeLoadAccountInfo = () => {
    const { accountStore } = this.props;
    accountStore
      .loadAccount()
      .then(() => {
        this.fieldInitialFill();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  fieldInitialFill = () => {
    const { accountStore } = this.props;
    this.form.fields
      .get('firstname')
      .setValue(accountStore.account.personal_info.firstname);
    this.form.fields
      .get('lastname')
      .setValue(accountStore.account.personal_info.lastname);
    this.form.fields
      .get('email')
      .setValue(accountStore.account.personal_info.email);
    this.form.fields
      .get('telephone')
      .setValue(accountStore.account.personal_info.telephone);
    this.form.validate();
  };

  handleSubmit = (e) => {
    const { apiWrapper } = this.props;

    e.preventDefault();
    this.form.validate();
    if (this.form.valid) {
      this.setState({
        status: UpdateStatus.REQUESTING
      });
      apiWrapper
        .apiAxios()
        .put('my-account/info', this.form.getFieldValues())
        .then(() => {
          this.setState({
            status: UpdateStatus.SUCCESS
          });
        })
        .catch((e) => {
          const status = e.response ? e.response.status : 500;
          const errorMap = {
            400: UpdateStatus.VALIDATION_ERROR,
            409: UpdateStatus.EMAIL_EXISTS,
            422: UpdateStatus.VALIDATION_ERROR,
            500: UpdateStatus.UNEXPECTED_ERROR
          };
          this.setState({
            status: errorMap[status]
          });
        });
    }
  };

  getField = (name, message) => {
    return (
      <div
        className={`MyAccountInfoForm__field MyAccountInfoForm__field--${name}`}
      >
        <FormGroupField
          field={this.form.fields.get(name)}
          label={<FormattedMessage {...message} />}
          formName="MyAccountInfoForm"
          fieldName={name}
        />
      </div>
    );
  };

  getValue = (value) => {
    if (!value || value.length === 0) {
      return '';
    }
    return value;
  };

  getForm = () => {
    return (
      <Form noValidate onSubmit={this.handleSubmit}>
        <Row>
          <Col md="6">
            <div className="MyAccountInfoForm__row">
              {this.getField('firstname', globalTranslations.firstNameTitle)}
              {this.getField('lastname', globalTranslations.lastNameTitle)}
              {this.getField('email', globalTranslations.emailTitle)}
              {this.getField('telephone', globalTranslations.telephoneTitle)}
              <Button
                className="MyAccountInfoForm__submit"
                color="primary"
                disabled={
                  this.form.valid === false ||
                  this.state.error === UpdateStatus.REQUESTING
                }
              >
                {<FormattedMessage {...globalTranslations.update} />}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    );
  };

  renderStatus = (status) => {
    const { configStore } = this.props;
    let message = null;

    switch (status) {
      case UpdateStatus.NONE:
      case UpdateStatus.REQUESTING:
        break;
      case UpdateStatus.SUCCESS:
        message = (
          <Alert color="success">
            <FormattedMessage
              id="account.userInfo.SUCCESS"
              defaultMessage="Your customer account information was updated successfully."
            />
          </Alert>
        );
        break;
      case UpdateStatus.VALIDATION_ERROR:
        // This is just in case, as it should actually never happen, because we validate the fields client side.
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.userInfo.VALIDATION_ERROR"
              defaultMessage="The information you entered is not valid. Please check the form."
            />
          </Alert>
        );
        break;
      case UpdateStatus.FIRSTNAME_NOT_VALID:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.userInfo.FIRSTNAME_NOT_VALID"
              defaultMessage="The first name must contain at least {characterCount} characters."
              values={{
                characterCount: configStore.formValidation.firstName.minLength
              }}
            />
          </Alert>
        );
        break;
      case UpdateStatus.LASTNAME_NOT_VALID:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.userInfo.LASTNAME_NOT_VALID"
              defaultMessage="The last name must contain at least {characterCount} characters."
              values={{
                characterCount: configStore.formValidation.lastName.minLength
              }}
            />
          </Alert>
        );
        break;
      case UpdateStatus.EMAIL_NOT_VALID:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.userInfo.EMAIL_NOT_VALID"
              defaultMessage="Your e-mail address seems to have an incorrect format."
            />
          </Alert>
        );
        break;
      case UpdateStatus.TELEPHONE_NOT_VALID:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.userInfo.TELEPHONE_NOT_VALID"
              defaultMessage="The phone number must contain at least {characterCount} characters."
              values={{
                characterCount: configStore.formValidation.telephone.minLength
              }}
            />
          </Alert>
        );
        break;
      default:
      case UpdateStatus.UNEXPECTED_ERROR:
        message = <UnexpectedError />;
    }
    return message;
  };

  render() {
    const { accountStore } = this.props;
    return (
      <ContentForState
        state={accountStore.state}
        error={accountStore.lastError}
        forLoaded={() => {
          return (
            <div className="MyAccountInfoForm">
              {this.renderStatus(this.state.status)}
              {this.state.status !== null && this.getForm()}
            </div>
          );
        }}
      />
    );
  }
}

MyAccountInfoForm.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  apiWrapper: PropTypes.instanceOf(ApiWrapper).isRequired
};

export default inject('apiWrapper', 'configStore', 'accountStore')(
  MyAccountInfoForm
);



// WEBPACK FOOTER //
// ./src/components/my-account/MyAccountInfoForm/index.jsx